import React, { ReactNode, useState } from 'react';
import { Button, Modal, Form } from 'react-bootstrap';
import AuthHeadingPanel from 'components/Auth/Common/AuthHeadingPanel';
import useTranslate from 'hooks/useTranslate';
import { useFormik } from 'formik';
import { Link, useNavigate } from 'react-router-dom';
import { signInInitialValues, signInSchema } from 'constants/schemaValidations';
import { logEmailLogin, useLoginMutation } from 'services';
import { SignInInterface } from 'constants/interfaces';
import { USER_TYPES } from 'constants/index';
import { setPreviewsRole, setUserInfo } from 'store/auth';
import { setTempLoginData } from 'store/user';
import { getBrowserID } from 'utils';
import useCommonStore from 'store/common';
import toast from 'react-hot-toast';
import SocialLogin from 'components/Auth/Common/SocialLogin';
import { secureStorage } from 'utils/secureStorage';

const LoginModal: React.FC<any> = () => {
	const { hideCommonModal }: any = useCommonStore((state) => state);
	const [userType, setUserType] = useState(USER_TYPES?.SEEKER);
	const { translate } = useTranslate();
	const navigate = useNavigate();

	// mutation
	const { mutateAsync: loginMutation, isPending } = useLoginMutation();

	const [showPassword, setShowPassword] = useState(false);

	const togglePasswordVisibility = () => {
		setShowPassword((prev) => !prev);
	};

	// storage
	const loginInfo = secureStorage.getItem('loginInfo') || {};

	const formik = useFormik({
		initialValues: { ...signInInitialValues, ...loginInfo },
		validationSchema: signInSchema(translate),
		onSubmit: async (values) => {
			const fcmDeviceToken = secureStorage.getItem('fcmToken') || '';
			loginMutation({
				...values,
				browserToken: getBrowserID(),
				userType: userType || USER_TYPES.SEEKER,
				deviceToken: fcmDeviceToken,
			})
				.then((res: any) => {
					if (res?.success) {
						if (res?.data?.user?.checkTwoFactorAuth) {
							setTempLoginData(res?.data);
							navigate('/two-factor-authentication');
							return;
						}
						setUserInfo(res?.data);
						setPreviewsRole(res?.data?.user?.userType);
						if (formik.values.rememberMe) {
							secureStorage.setItem('loginInfo', {
								email: values.email,
								rememberMe: formik.values.rememberMe,
							});
						} else {
							secureStorage.removeItem('loginInfo');
						}
						hideCommonModal();
					}
				})
				.catch((error) => {
					console.error('Login mutation error:', error);
					toast.error(translate('ToastMessage.anUnexpectedErrorOccurred')); // Notify the user of the failure
				})
				.finally(() => {
					logEmailLogin();
				});
		},
	});

	const renderError = <T extends keyof SignInInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	return (
		<Modal.Body className="auth-modal-body">
			<Button
				className="close-btn rounded-circle border-0 fw-bold position-absolute"
				onClick={() => hideCommonModal()}
			>
				<i className="ri-close-line"></i>
			</Button>
			<div className="auth-form mx-auto">
				<AuthHeadingPanel description={translate('Auth.authHeadingLoginPanelDescription')} />
				{userType !== USER_TYPES?.COMPANY && (
					<>
						<SocialLogin type="login" />
						<div className="or-separator d-flex">
							<p className="mb-0 rounded-circle text-center">{translate('Common.or')}</p>
						</div>
					</>
				)}
				<div className="auth-form-fields">
					<Form className="form" onSubmit={formik.handleSubmit}>
						<div className="auth-form-group">
							<Form.Group className="mb-2 position-relative">
								<Form.Label className="lh-1">{translate('PlaceHolder.email')}</Form.Label>
								<Form.Control
									type="email"
									placeholder={translate('PlaceHolder.emailText1')}
									name="email"
									className="rounded-pill"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.email}
									autoComplete="off"
								/>
								{renderError('email')}
							</Form.Group>
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('PlaceHolder.password')}</Form.Label>
								<Form.Control
									type={showPassword ? 'text' : 'password'}
									name="password"
									placeholder={translate('PlaceHolder.passwordText', { field: '8' })}
									className="rounded-pill"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.password}
									autoComplete="off"
								/>
								<i
									className={`ri-eye${!showPassword ? '-off' : ''}-line form-icon position-absolute lh-1`}
									onClick={() => togglePasswordVisibility()}
								></i>
								{renderError('password')}
							</Form.Group>
						</div>

						<div className="d-flex align-items-center justify-content-between">
							<Form.Check
								type="checkbox"
								className="mb-0"
								name="rememberMe"
								label={translate('PlaceHolder.rememberMe')}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								checked={formik.values.rememberMe}
								id="rememberMeLoginModal"
							></Form.Check>
							<Link
								to={`/forgot-password?userType=${userType || USER_TYPES.SEEKER}`}
								className="forgot-password"
							>
								{translate('Common.forgotPassword')}
							</Link>
						</div>
						<div className="form-btn">
							<Button className="filled-btn rounded-pill w-100" type="submit" disabled={isPending}>
								{translate('Common.login')}
							</Button>
						</div>
						<p className="mb-0 text-center">
							{translate('Common.doNotHaveAccount')}{' '}
							<Link
								to={`/signup?userType=${userType || USER_TYPES.SEEKER}`}
								className="fw-bold join-link"
							>
								{translate('Common.joinNow')}
							</Link>
						</p>
						<div className="text-center mt-1">
							<Link
								to={{}}
								onClick={() =>
									setUserType(
										userType === USER_TYPES.SEEKER ? USER_TYPES.COMPANY : USER_TYPES.SEEKER,
									)
								}
								className="fw-bold join-link text-uppercase text-decoration-underline"
							>
								{translate('Modal.loginAs')}{' '}
								{userType === USER_TYPES.SEEKER ? translate('Job.employer') : USER_TYPES.SEEKER}
							</Link>
						</div>
					</Form>
				</div>
			</div>
		</Modal.Body>
	);
};

export default LoginModal;
